import { useState, useEffect, useRef, useCallback, type ReactNode } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, ChevronLeft, ChevronRight, Eye, EyeOff, AlertCircle, Loader2, Inbox } from 'lucide-react'
import QuestionViewer from './QuestionViewer'
import QuestionListItem from './QuestionListItem'
import { useNodeQuestions, useQuestion, questionQueryOptions } from '../hooks/usePYQ'
import { fbTrackPYQView } from '@/shared/utils/facebookPixel'

interface NodeQuestionBrowserProps {
  nodeCode: string
  nodeName: string
  /** Two panes (list + reading pane). Otherwise phone flow: list, then the question as its own view */
  isWide: boolean
  /** Rendered above the list (hidden on phones while a question is open) */
  header?: ReactNode
}

/**
 * Gmail-style browser for the PYQs mapped to a syllabus node.
 * The list comes from the batch summary API; the open question is loaded in full on demand.
 * The open question lives in the URL as ?q=<1-based number>.
 */
export default function NodeQuestionBrowser({ nodeCode, nodeName, isWide, header }: NodeQuestionBrowserProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const [showSolution, setShowSolution] = useState(false)
  const listRef = useRef<HTMLUListElement>(null)
  const fromList = (location.state as { fromList?: boolean } | null)?.fromList ?? false

  const { data: questions = [], isLoading, isError, refetch } = useNodeQuestions(nodeCode)
  const totalQuestions = questions.length

  const qParam = Number(searchParams.get('q'))
  const hasSelection = Number.isInteger(qParam) && qParam >= 1 && qParam <= totalQuestions
  // Wide screens always show a question (the first by default); phones show the list until one is opened
  const selectedIndex = hasSelection ? qParam - 1 : isWide && totalQuestions > 0 ? 0 : null
  const selectedId = selectedIndex !== null ? questions[selectedIndex]?.questionId : undefined
  const showList = isWide || selectedIndex === null

  const {
    data: currentQuestion,
    isLoading: isQuestionLoading,
    isError: isQuestionError,
    refetch: refetchQuestion,
  } = useQuestion(selectedId ?? '', !!selectedId)

  const withQuestion = useCallback(
    (q: number | null) => {
      const next = new URLSearchParams(searchParams)
      if (q === null) next.delete('q')
      else next.set('q', String(q))
      return `?${next}`
    },
    [searchParams]
  )

  const selectQuestion = useCallback(
    (index: number) => {
      // Phone list -> question is a new view (own history entry); everything else just swaps the question
      const opensNewView = !isWide && !hasSelection
      navigate(
        { search: withQuestion(index + 1) },
        { state: { fromList: opensNewView || fromList }, replace: !opensNewView }
      )
    },
    [isWide, hasSelection, fromList, navigate, withQuestion]
  )

  const backToList = () => {
    if (fromList) {
      navigate(-1)
    } else {
      navigate({ search: withQuestion(null) }, { replace: true })
    }
  }

  const goToPrev = useCallback(() => {
    if (selectedIndex !== null && selectedIndex > 0) selectQuestion(selectedIndex - 1)
  }, [selectedIndex, selectQuestion])

  const goToNext = useCallback(() => {
    if (selectedIndex !== null && selectedIndex < totalQuestions - 1) selectQuestion(selectedIndex + 1)
  }, [selectedIndex, totalQuestions, selectQuestion])

  // Track ViewContent for FB Pixel when the node's questions are loaded
  useEffect(() => {
    if (totalQuestions > 0) fbTrackPYQView('syllabus', nodeCode, nodeName)
  }, [nodeCode, nodeName, totalQuestions])

  // Reset solution visibility when question changes
  useEffect(() => {
    setShowSolution(false)
  }, [selectedId])

  // Load the next question ahead of time so stepping through feels instant
  useEffect(() => {
    if (selectedIndex === null) return
    const nextId = questions[selectedIndex + 1]?.questionId
    if (nextId) queryClient.prefetchQuery(questionQueryOptions(nextId))
  }, [selectedIndex, questions, queryClient])

  // Keep the selected row visible in the list pane
  useEffect(() => {
    if (!isWide || selectedIndex === null) return
    listRef.current
      ?.querySelector(`[data-question-index="${selectedIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' })
  }, [isWide, selectedIndex])

  // Gmail-style keyboard navigation: j = next, k = previous
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (e.target instanceof HTMLElement && e.target.closest('input, textarea, select')) return
      if (e.key === 'j') goToNext()
      if (e.key === 'k') goToPrev()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [goToNext, goToPrev])

  if (isLoading) {
    return (
      <div className="flex h-full flex-col">
        {header}
        <div className="flex flex-1 items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      </div>
    )
  }

  if (isError || totalQuestions === 0) {
    return (
      <div className="flex h-full flex-col">
        {header}
        <div className="flex flex-1 items-center justify-center p-6 text-center">
          <div>
            {isError ? (
              <>
                <AlertCircle className="mx-auto mb-3 h-10 w-10 text-red-500" />
                <h3 className="font-medium text-gray-800">Couldn't load questions</h3>
                <button onClick={() => refetch()} className="mt-2 text-sm font-medium text-blue-600 underline">
                  Try again
                </button>
              </>
            ) : (
              <>
                <Inbox className="mx-auto mb-3 h-10 w-10 text-gray-400" />
                <h3 className="font-medium text-gray-700">No previous year questions yet</h3>
                <p className="mt-1 text-sm text-gray-500">None are mapped to {nodeName}.</p>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    // `relative` on the scroll containers keeps KaTeX's absolutely-positioned hidden
    // MathML clipped inside them; otherwise it stretches the page and adds a window scrollbar
    <div className="relative flex h-full flex-col overflow-hidden">
      {showList && header}

      <div className="flex min-h-0 flex-1">
        {/* Question list */}
        {showList && (
          <aside
            className="relative w-full overflow-y-auto bg-white md:w-72 md:flex-shrink-0 md:border-r md:border-gray-200"
            aria-label="Questions"
          >
            <ul ref={listRef}>
              {questions.map((question, index) => (
                <QuestionListItem
                  key={question.questionId}
                  question={question}
                  questionNumber={index + 1}
                  selected={index === selectedIndex}
                  onSelect={selectQuestion}
                />
              ))}
            </ul>
          </aside>
        )}

        {/* Reading pane */}
        {selectedIndex !== null && (
          <section className="flex min-w-0 flex-1 flex-col bg-white">
            <div className="flex flex-shrink-0 items-center gap-2 border-b border-gray-200 px-3 py-2">
              {!isWide && (
                <>
                  <button
                    onClick={backToList}
                    aria-label="Back to questions"
                    className="rounded-full p-2 text-gray-600 hover:bg-gray-100"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <span className="min-w-0 truncate text-sm font-medium text-gray-700">{nodeName}</span>
                </>
              )}

              <div className="ml-auto flex items-center gap-1">
                <button
                  onClick={() => setShowSolution((v) => !v)}
                  disabled={!currentQuestion}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 ${
                    showSolution
                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {showSolution ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  <span>{showSolution ? 'Hide' : 'Solution'}</span>
                </button>
                <span className="hidden px-2 text-sm text-gray-500 sm:inline">
                  {selectedIndex + 1} of {totalQuestions}
                </span>
                <button
                  onClick={goToPrev}
                  disabled={selectedIndex === 0}
                  aria-label="Previous question"
                  className="rounded-full p-2 text-gray-600 hover:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={goToNext}
                  disabled={selectedIndex >= totalQuestions - 1}
                  aria-label="Next question"
                  className="rounded-full p-2 text-gray-600 hover:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="relative min-h-0 flex-1">
              {currentQuestion ? (
                <QuestionViewer
                  question={currentQuestion}
                  questionNumber={selectedIndex + 1}
                  totalQuestions={totalQuestions}
                  showSolution={showSolution}
                  // Access is gated by the pages (redeemed activation key), not the account type
                  />
              ) : isQuestionLoading ? (
                <div className="flex h-full items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                </div>
              ) : isQuestionError ? (
                <div className="flex h-full items-center justify-center text-center">
                  <div>
                    <AlertCircle className="mx-auto mb-3 h-10 w-10 text-red-500" />
                    <h3 className="font-medium text-gray-800">Couldn't load this question</h3>
                    <button
                      onClick={() => refetchQuestion()}
                      className="mt-2 text-sm font-medium text-blue-600 underline"
                    >
                      Try again
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </section>
        )}
      </div>
    </div>
  )
}
